<script setup lang="ts">
import type { CheckSendAvailabilityCardResult } from './types'
import { WarningFilled } from '@element-plus/icons-vue'
import { computed, onMounted, ref, watch } from 'vue'
import { useCheckSendAvailability } from './useCheckSendAvailability'

const props = withDefaults(defineProps<{
  goodCode?: string | number | null
  goodType?: string | null
  countryCode?: string | null
  matchTransportGoods?: boolean
  transportCategoryCode?: string | number | null
  transportCategoryName?: string | null
  transportCategoryPath?: string | null
  title?: string
  description?: string
  categories?: string[]
  count?: number | null
  autoQuery?: boolean
  showWhenAllowed?: boolean
  noticeUrl?: string
  contactUrl?: string
}>(), {
  goodCode: '',
  goodType: '',
  countryCode: '',
  matchTransportGoods: false,
  transportCategoryCode: '',
  transportCategoryName: '',
  transportCategoryPath: '',
  title: '',
  description: '',
  categories: () => [],
  count: null,
  autoQuery: true,
  showWhenAllowed: false,
  noticeUrl: '/v?url=disabled_transport_detail',
  contactUrl: '/v?url=contact_us',
})

const emit = defineEmits<{
  (event: 'result', payload: CheckSendAvailabilityCardResult): void
  (event: 'contact'): void
  (event: 'notice'): void
}>()

const { t } = useI18n()
const { querying, queryAvailability, shopId } = useCheckSendAvailability()

const queried = ref(false)
const isAllowed = ref(true)
const logistics = ref<any[]>([])

const normalizedGoodCode = computed(() => String(props.goodCode || '').trim())
const normalizedGoodType = computed(() => String(props.goodType || '').trim())
const normalizedCountryCode = computed(() => String(props.countryCode || '').trim())
const normalizedTransportCategoryCode = computed(() => String(props.transportCategoryCode || '').trim())
const normalizedTransportCategoryName = computed(() => String(props.transportCategoryName || '').trim())
const normalizedTransportCategoryPath = computed(() => String(props.transportCategoryPath || '').trim())

const blockedCategories = computed(() => {
  const list = Array.isArray(props.categories) ? props.categories : []
  return list.map(item => String(item || '').trim()).filter(Boolean)
})

const fallbackBlockedCategory = computed(() => {
  const text = normalizedGoodType.value
  if (!text) {
    return ''
  }

  return text.split('/')[0].trim()
})

const displayBlockedCategories = computed(() => {
  if (queried.value && isAllowed.value && !props.showWhenAllowed) {
    return []
  }

  if (blockedCategories.value.length) {
    return blockedCategories.value
  }

  if (!isAllowed.value && fallbackBlockedCategory.value) {
    return [fallbackBlockedCategory.value]
  }

  return []
})

const blockedCount = computed(() => {
  const rawCount = Number(props.count)
  if (Number.isFinite(rawCount) && rawCount > 0) {
    return rawCount
  }

  return displayBlockedCategories.value.length || 1
})

const shouldShowCard = computed(() => {
  if (!queried.value) {
    return false
  }

  if (props.showWhenAllowed) {
    return true
  }

  return !isAllowed.value || displayBlockedCategories.value.length > 0
})

const cardTitle = computed(() => {
  return props.title || t('checkSendPackage.restrictedTitle')
})

const cardDescription = computed(() => {
  return props.description || t('checkSendPackage.restrictedDescription')
})

function emitResult() {
  emit('result', {
    isAllowed: isAllowed.value,
    logistics: logistics.value,
    countryCode: normalizedCountryCode.value,
    goodCode: normalizedGoodCode.value,
    goodType: normalizedGoodType.value,
  })
}

async function runQuery() {
  if (!props.autoQuery) {
    return
  }

  if (!normalizedCountryCode.value || (!normalizedGoodCode.value && !normalizedGoodType.value)) {
    return
  }

  try {
    const result = await queryAvailability({
      countryCode: normalizedCountryCode.value,
      goodCode: normalizedGoodCode.value,
      goodType: normalizedGoodType.value,
      matchTransportGoods: props.matchTransportGoods,
      transportCategoryCode: normalizedTransportCategoryCode.value,
      transportCategoryName: normalizedTransportCategoryName.value,
      transportCategoryPath: normalizedTransportCategoryPath.value,
      showError: false,
    })
    queried.value = true
    isAllowed.value = result.isAllowed
    logistics.value = result.list
    emitResult()
  }
  catch {
    queried.value = true
    isAllowed.value = true
    logistics.value = []
    emitResult()
  }
}

function handleContact() {
  emit('contact')
  if (process.client && props.contactUrl) {
    window.open(props.contactUrl, '_blank')
  }
}

function handleNotice() {
  emit('notice')
  if (process.client && props.noticeUrl) {
    window.open(props.noticeUrl, '_blank')
  }
}

watch(
  () => [
    shopId.value,
    normalizedCountryCode.value,
    normalizedGoodCode.value,
    normalizedGoodType.value,
    props.matchTransportGoods,
    normalizedTransportCategoryCode.value,
    normalizedTransportCategoryName.value,
    normalizedTransportCategoryPath.value,
  ],
  () => {
    queried.value = false
    isAllowed.value = true
    logistics.value = []
    runQuery()
  },
)

onMounted(() => {
  runQuery()
})
</script>

<template>
  <section v-if="shouldShowCard" class="check-send-availability-card">
    <div class="check-send-availability-card__header">
      <div class="check-send-availability-card__header-main">
        <div class="check-send-availability-card__icon-wrap">
          <el-icon class="check-send-availability-card__icon">
            <WarningFilled />
          </el-icon>
        </div>

        <div class="check-send-availability-card__text">
          <h3>{{ cardTitle }}</h3>
          <p>{{ cardDescription }}</p>
        </div>
      </div>

      <div class="check-send-availability-card__count">
        {{ blockedCount }}
      </div>
    </div>

    <div class="check-send-availability-card__body">
      <div class="check-send-availability-card__content">
        <p class="check-send-availability-card__label">
          {{ t('checkSendPackage.restrictedCategoriesLabel') }}
        </p>

        <div v-if="displayBlockedCategories.length" class="check-send-availability-card__tags">
          <span
            v-for="category in displayBlockedCategories"
            :key="category"
            class="check-send-availability-card__tag"
          >
            {{ category }}
          </span>
        </div>

        <div v-else-if="querying" class="check-send-availability-card__placeholder">
          {{ t('checkSendPackage.querying') }}
        </div>

        <div v-else class="check-send-availability-card__placeholder">
          {{ isAllowed ? t('checkSendPackage.allowed') : t('checkSendPackage.notAllowed') }}
        </div>
      </div>

      <div class="check-send-availability-card__actions">
        <el-button
          class="check-send-availability-card__primary"
          :loading="querying"
          @click="handleContact"
        >
          {{ t('texts.contactClient') }}
        </el-button>

        <el-button class="check-send-availability-card__ghost" @click="handleNotice">
          {{ t('checkSendPackage.viewNotice') }}
        </el-button>
      </div>
    </div>
  </section>
</template>

<style scoped lang="scss">
.check-send-availability-card {
  background: linear-gradient(180deg, #fffaf4 0%, #fff 100%);
  border: 1px solid #f6c28d;
  border-radius: 24px;
  box-shadow: 0 12px 32px -24px rgba(255, 114, 40, 0.55);
  margin: 18px 0 22px;
  overflow: hidden;
}

.check-send-availability-card__header {
  align-items: flex-start;
  background: linear-gradient(135deg, #fff7ea 0%, #fff1dc 100%);
  display: flex;
  gap: 16px;
  justify-content: space-between;
  padding: 20px 28px 16px;
}

.check-send-availability-card__header-main {
  align-items: flex-start;
  display: flex;
  gap: 16px;
  min-width: 0;
}

.check-send-availability-card__icon-wrap {
  align-items: center;
  background: linear-gradient(135deg, #ffb36b 0%, #ff7a18 100%);
  border-radius: 999px;
  color: #fff;
  display: flex;
  flex: 0 0 48px;
  height: 48px;
  justify-content: center;
  width: 48px;
}

.check-send-availability-card__icon {
  font-size: 22px;
}

.check-send-availability-card__text h3 {
  color: #bf4b00;
  font-size: 22px;
  font-weight: 800;
  line-height: 1.35;
  margin: 0;
}

.check-send-availability-card__text p {
  color: #8c5c35;
  font-size: 14px;
  line-height: 1.6;
  margin: 8px 0 0;
  max-width: 760px;
}

.check-send-availability-card__count {
  align-items: center;
  background: #fff;
  border: 1px solid #f1bd8a;
  border-radius: 999px;
  color: #d86b10;
  display: inline-flex;
  flex: 0 0 44px;
  font-size: 20px;
  font-weight: 700;
  height: 44px;
  justify-content: center;
  min-width: 44px;
}

.check-send-availability-card__body {
  align-items: flex-end;
  display: flex;
  gap: 24px;
  justify-content: space-between;
  padding: 18px 28px 24px;
}

.check-send-availability-card__content {
  flex: 1;
  min-width: 0;
}

.check-send-availability-card__label {
  color: #8c5c35;
  font-size: 14px;
  font-weight: 600;
  margin: 0;
}

.check-send-availability-card__tags {
  display: flex;
  flex-wrap: wrap;
  gap: 10px;
  margin-top: 14px;
}

.check-send-availability-card__tag {
  background: #fff4ea;
  border: 1px solid #ffc58f;
  border-radius: 999px;
  color: #b35d15;
  display: inline-flex;
  font-size: 14px;
  font-weight: 700;
  line-height: 20px;
  padding: 8px 16px;
}

.check-send-availability-card__placeholder {
  color: #9a6c43;
  font-size: 13px;
  line-height: 20px;
  margin-top: 12px;
}

.check-send-availability-card__actions {
  display: flex;
  flex: 0 0 auto;
  gap: 12px;
  justify-content: flex-end;
  margin-top: 0;
}

.check-send-availability-card__primary,
.check-send-availability-card__ghost {
  border-radius: 999px;
  font-size: 16px;
  font-weight: 700;
  height: 48px;
  padding: 0 22px;
  white-space: nowrap;
}

.check-send-availability-card__primary {
  background: linear-gradient(135deg, #ff8c26 0%, #ff6a00 100%);
  border-color: transparent;
  color: #fff;
  min-width: 156px;
      max-width: 200px;
    white-space: pre-wrap;
}

.check-send-availability-card__ghost {
  background: #fff8f1;
  border-color: #f6c28d;
  color: #d26913;
  min-width: 156px;
}

@media (max-width: 768px) {
  .check-send-availability-card__header {
    flex-direction: column;
    padding: 20px 20px 16px;
  }

  .check-send-availability-card__text h3 {
    font-size: 22px;
  }

  .check-send-availability-card__text p {
    font-size: 14px;
  }

  .check-send-availability-card__body {
    align-items: stretch;
    flex-direction: column;
    padding: 16px 20px 20px;
  }

  .check-send-availability-card__actions {
    flex-direction: column;
    width: 100%;
  }

  .check-send-availability-card__primary,
  .check-send-availability-card__ghost {
    width: 100%;
  }
}
</style>
